/**
 * Tenant-wide inventory custom-field schema CRUD.
 *
 * Mirrors `lib/customer-custom-fields.ts` exactly, just at a different
 * scope. The shared `<CustomFieldsManager />` UI binds to the same
 * `CustomField` / `CustomFieldInput` types defined in `service-detail.ts`.
 *
 * Endpoints: `/api/v1/inventory-fields/*` — see
 * `InventoryCustomFieldController`.
 */
import { useQuery } from "@tanstack/react-query";
import { authFetch, authFetchJson } from "./auth-fetch";
import { throwIfNotOk } from "./api-error";
import { useAuthStore } from "./auth";
import type { CustomField, CustomFieldInput } from "./service-detail";

export type InventoryFieldsResponse = { fields: CustomField[] };

export function useInventoryCustomFields() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["inventory.custom-fields"],
    enabled: !!accessToken,
    queryFn: () =>
      authFetchJson<InventoryFieldsResponse>(`/api/v1/inventory-fields`),
  });
}

export async function createInventoryCustomField(
  body: CustomFieldInput,
): Promise<CustomField> {
  return authFetchJson<CustomField>(`/api/v1/inventory-fields`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateInventoryCustomField(
  fieldId: string,
  body: Partial<CustomFieldInput>,
): Promise<CustomField> {
  return authFetchJson<CustomField>(`/api/v1/inventory-fields/${fieldId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteInventoryCustomField(
  fieldId: string,
): Promise<void> {
  const res = await authFetch(`/api/v1/inventory-fields/${fieldId}`, {
    method: "DELETE",
  });
  await throwIfNotOk(res);
}

export async function reorderInventoryCustomFields(
  ids: string[],
): Promise<void> {
  const res = await authFetch(`/api/v1/inventory-fields/reorder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  await throwIfNotOk(res);
}
